import type {
  AlineSelection,
  LinePointPosition,
  RenderModelLine,
  RenderModelPage,
  RenderModelParagraph,
  RenderModelTable,
  RenderModelTableCell,
  RenderModelTableRow,
  SelBoundaryNode,
} from '@aline/common';
import {
  AlineTransforms,
  EditingMode,
  isParagraphElement,
  isRenderModelLine,
  isTableCellElement,
  isTableElement,
  isTableRowElement,
  SelEventSource,
} from '@aline/common';
import type { NodeEntry, TextUnit } from 'slate';
import { Editor, Path, Point, Range, Text } from 'slate';
import {
  getEditorTabStateActionsForTabId,
  getEditorTabStateForTabId,
} from 'src/editor/editor-tab-state-registry';
import { Queries } from 'src/editor/queries';
import type { AlineClientEditor } from 'src/layout-engine';
import { EDITOR_TO_TAB_STATE } from 'src/layout-engine/utils/weak-maps';

import { ClientTransforms } from '.';

const CLICK_THRESHOLD = 500;

export interface MouseDownSelectOptions {
  authorId: string;
  editingMode: EditingMode;
  isShift: boolean;
  pointerX: number;
  pointerY: number;
  rMPageEntry: NodeEntry<RenderModelPage>;
  dispatchClick?: boolean;
  forceAllowHeaderAndFooter?: boolean;
  /** Skip focusing the editor after selecting — used on mobile so a tap doesn't surface the soft keyboard. */
  skipFocus?: boolean;
  skipScrollSelectionIntoView?: boolean;
}

export interface MouseMoveSelectOptions {
  pointerX: number;
  pointerY: number;
  rMPageEntry: NodeEntry<RenderModelPage>;
}

export const selectionTransforms = {
  /**
   * Adjust the selection based on a mouse down click in a render model container node.
   */
  mouseDownSelect: (editor: Editor, options: MouseDownSelectOptions): void => {
    const {
      authorId,
      dispatchClick = true,
      editingMode,
      forceAllowHeaderAndFooter = false,
      isShift,
      pointerX,
      pointerY,
      rMPageEntry,
      skipFocus,
      skipScrollSelectionIntoView = false,
    } = options;
    /**
     * Focus the editor at the beginning of the mouse down since later in the function we might return
     * (e.g., if a line is not found) and we always want clicks on the page to focus the editor.
     * Note: Do not use AlineTransforms.focus here since that function uses Promise.resolve and may steal focus from descendants that try and use focus
     * (e.g., inputs inside popovers).
     *
     * On mobile, callers may set skipFocus=true to avoid focusing the hidden contenteditable
     * (which would surface the iOS keyboard during read-only/signing flows).
     */
    if (!skipFocus) ClientTransforms.focus(editor);

    const tabId = EDITOR_TO_TAB_STATE.get(editor)?.tabId ?? null;
    const editorTabState = getEditorTabStateForTabId(tabId);

    const clickedObject = Queries.resolvePointerToClickable(editor, {
      isFooterOpen: forceAllowHeaderAndFooter || Boolean(editorTabState?.documentUi.footerOpen),
      isHeaderOpen: forceAllowHeaderAndFooter || Boolean(editorTabState?.documentUi.headerOpen),
      pointerX,
      pointerY,
      rMElementEntry: rMPageEntry,
    });
    if (!clickedObject) return;

    if (clickedObject.type === 'list-marker') {
      AlineTransforms.selectListMarker(
        editor,
        Queries.getCMPathFromRMPath(editor, clickedObject.rMParagraphEntry[1])
      );
      return;
    }

    if (clickedObject.type === 'ensure-paragraph-at-end') {
      if (editingMode === EditingMode.EDITING || editingMode === EditingMode.SUGGESTING) {
        AlineTransforms.ensureParagraphAtEnd(editor, {
          authorId,
          containerEntry: Queries.getDocument(editor),
          editingMode,
        });
      }
      return;
    }

    const { affinity, cMPoint: point, lineEdge } = clickedObject;

    // Which grid column the pointer was over, to anchor a merged cell's column (null off any table).
    const pointGridColumn = Queries.getTableGridColumnAtPointer(editor, {
      pointerX,
      pointerY,
      rMPageEntry,
    });

    if (dispatchClick) {
      const anchorPoint = isShift && editor.selection ? editor.selection.anchor : point;
      // On shift-click keep the existing anchor's captured column; otherwise anchor on this click.
      const anchorGridColumn =
        isShift && editor.selection
          ? (editor.selection.gridSelectionAnchorColumn ?? null)
          : pointGridColumn;

      const resolvedSelection = ClientTransforms.resolveSelectionFromMouseEventPoints(editor, {
        consecutiveClickCount: editorTabState?.click.consecutiveClickCount ?? 1,
        lastMouseDownPoint: editorTabState?.click.lastMouseDownPoint ?? null,
        lastMouseDownTimestamp: editorTabState?.click.lastMouseDownTimestamp ?? null,
        lineEdge,
        mouseEventPoints: { anchor: anchorPoint, focus: point },
      });
      AlineTransforms.setSelection(editor, {
        eventSource: SelEventSource.MOUSE,
        props: {
          ...resolvedSelection.range,
          affinity,
          gridSelectionAnchorColumn: anchorGridColumn,
          gridSelectionFocusColumn: pointGridColumn,
        },
      });

      if (tabId) {
        const actions = getEditorTabStateActionsForTabId(tabId);
        actions.click.setClickCount(resolvedSelection.consecutiveClickCount);
        actions.click.setMouseDownClick({
          gridColumn: anchorGridColumn,
          point: anchorPoint,
          skipScrollSelectionIntoView,
          timestamp: performance.now(),
        });
        actions.click.setLastMouseDownPoint(point);
        actions.click.setLastMouseDownTimestamp(performance.now());
      }
    } else {
      AlineTransforms.setSelection(editor, {
        eventSource: SelEventSource.MOUSE,
        props: {
          affinity,
          anchor: point,
          focus: point,
          gridSelectionAnchorColumn: pointGridColumn,
          gridSelectionFocusColumn: pointGridColumn,
        },
      });
    }
  },

  /**
   * Adjust the selection based on a mouse move event in a render model container node.
   */
  mouseMoveSelect: (editor: Editor, options: MouseMoveSelectOptions): void => {
    const { pointerX, pointerY, rMPageEntry } = options;
    const tabId = EDITOR_TO_TAB_STATE.get(editor)?.tabId ?? null;
    const editorTabState = getEditorTabStateForTabId(tabId);
    const mouseDownClick = editorTabState?.click.mouseDownClick;
    if (!mouseDownClick) return;

    const pointDetails = Queries.resolvePointerToPoint(editor, {
      isFooterOpen: Boolean(editorTabState?.documentUi.footerOpen),
      isHeaderOpen: Boolean(editorTabState?.documentUi.headerOpen),
      pointerX,
      pointerY,
      rMElementEntry: rMPageEntry,
    });
    if (!pointDetails) return;

    const { point: anchor } = mouseDownClick;
    const { affinity, cMPoint: point } = pointDetails;
    const isForward = Point.equals(anchor, point) || Point.isBefore(anchor, point);
    const pointPosition = detectPointPositionRelativeToWord(editor, anchor);
    const consecutiveClickCount = editorTabState.click.consecutiveClickCount;

    // The anchor column was captured at mousedown; resolve the focus column at the pointer each move.
    const gridColumnProps: Partial<AlineSelection> = {
      gridSelectionAnchorColumn: mouseDownClick.gridColumn ?? null,
      gridSelectionFocusColumn: Queries.getTableGridColumnAtPointer(editor, {
        pointerX,
        pointerY,
        rMPageEntry,
      }),
    };

    const getNewSelection = (unit: TextUnit) => {
      /**
       * Word/line expansion crosses block boundaries, so clamp each expanded endpoint back into its
       * own table cell — otherwise expanding past a cell's content spills the focus into the next
       * cell (the next column) and pulls an extra column into the selection.
       */
      const newAnchor = Queries.clampPointToTableCell(editor, {
        candidate: isForward
          ? pointPosition.isAtWordStart
            ? anchor
            : Editor.before(editor, anchor, { distance: 1, unit })
          : pointPosition.isAtWordEnd
            ? anchor
            : Editor.after(editor, anchor, { distance: 1, unit }),
        referencePoint: anchor,
      });
      const newFocus = Queries.clampPointToTableCell(editor, {
        candidate: isForward
          ? Editor.after(editor, point, { distance: 1, unit })
          : Editor.before(editor, point, { distance: 1, unit }),
        referencePoint: point,
      });
      return { affinity, anchor: newAnchor, focus: newFocus };
    };

    switch (consecutiveClickCount) {
      case 3: {
        AlineTransforms.setSelection(editor, {
          eventSource: SelEventSource.MOUSE,
          props: { ...getNewSelection('line'), ...gridColumnProps },
        });
        break;
      }
      case 2: {
        AlineTransforms.setSelection(editor, {
          eventSource: SelEventSource.MOUSE,
          props: { ...getNewSelection('word'), ...gridColumnProps },
        });
        break;
      }
      case 1:
      case 0: {
        AlineTransforms.setSelection(editor, {
          eventSource: SelEventSource.MOUSE,
          props: { affinity, anchor, focus: point, ...gridColumnProps },
        });
      }
    }
  },

  moveSelVertically: (
    editor: AlineClientEditor,
    options: {
      authorId: string;
      direction: 'forward' | 'backward';
      edge: 'both' | 'focus';
      editingMode: EditingMode;
      eventSource?: SelEventSource;
    }
  ): void => {
    const {
      authorId,
      direction,
      edge,
      editingMode,
      eventSource = SelEventSource.KEYBOARD,
    } = options;
    if (!editor.selection) return;

    // Extending an active grid selection steps its focus cell a row, not the point line-by-line.
    if (edge === 'focus') {
      const rowDirection = direction === 'forward' ? 'down' : 'up';

      if (Queries.getGridSelectionInfo(editor))
        return AlineTransforms.extendGridSelection(editor, {
          direction: rowDirection,
          eventSource,
        });

      // In the cross-table state (in-table edge snapped to whole rows), step one row instead.
      if (
        AlineTransforms.extendRowSelection(editor, {
          direction: rowDirection,
          eventSource,
          unit: 'line',
        })
      )
        return;
    }

    const rMLineEntry = Queries.getRMLinesAboveCMPoint(
      editor,
      editor.selection.focus,
      editor.selection.affinity ?? 'forward'
    ).next().value;
    if (!rMLineEntry) return;

    const { absWidthToFocus, adjacentLineEntry, relWidthToNewFocus } = getAdjacentLineEntry(
      editor,
      rMLineEntry,
      direction
    );

    if (!adjacentLineEntry) {
      handleOutOfBoundsSelectionMove();
      return;
    }

    const [adjacentLine, adjacentLinePath] = adjacentLineEntry;
    const rMParaEntryAboveAdjacentLine = Queries.renderModelAbove<RenderModelParagraph>(editor, {
      at: adjacentLinePath,
      match: isParagraphElement,
    });
    if (!rMParaEntryAboveAdjacentLine) return;

    const [, adjacentRMParaPath] = rMParaEntryAboveAdjacentLine;
    const adjacentCMParaPath = Queries.getCMPathFromRMPath(editor, adjacentRMParaPath);
    const newSelCrossesBoundary = Queries.newSelLocationCrossesBoundary(editor, {
      newSelLocation: adjacentCMParaPath,
    });

    // The selection should not cross a selection boundary
    if (newSelCrossesBoundary) {
      handleOutOfBoundsSelectionMove();
      return;
    }

    const pointDetails = Queries.resolvePointerToPoint(editor, {
      isFooterOpen: false,
      isHeaderOpen: false,
      pointerX: relWidthToNewFocus,
      pointerY: 0,
      rMElementEntry: adjacentLineEntry,
    });
    if (!pointDetails) return;
    const { cMPoint: point } = pointDetails;

    /**
     * For affinity, if we're selecting the end of the adjacent line, we want affinity backward so
     * the cursor renders on the end of the line. Forward in all other cases.
     */
    const absoluteEndPointCMPath: Path = adjacentCMParaPath.concat(
      adjacentLine.relativeEndPoint.path
    );
    const pointAtEnd = Point.equals(point, {
      ...adjacentLine.relativeEndPoint,
      path: absoluteEndPointCMPath,
    });
    const newSel: Partial<AlineSelection> = {
      affinity: pointAtEnd ? 'backward' : 'forward',
      anchor: edge === 'both' ? point : editor.selection.anchor,
      focus: point,
      moveVertWidth: absWidthToFocus,
    };

    if (Range.isCollapsed({ ...editor.selection, ...newSel })) {
      const rMEditor = Queries.buildRenderModelEditor(editor);
      AlineTransforms.announce(
        editor,
        Queries.string(rMEditor, { at: adjacentLinePath }) || 'new line'
      );
    }

    AlineTransforms.setSelection(editor, { eventSource, props: newSel });

    /**
     * Handle a selection move with no valid destination. If certain circumstances are met, we might
     * handle this by creating an empty paragraph at the end of the selection boundary. Otherwise,
     * we select the appropriate paragraph edge.
     */
    function handleOutOfBoundsSelectionMove() {
      const selBoundaryEntry = Queries.selectionBoundaryAboveLoc(editor);

      if (selBoundaryEntry && shouldEnsureParagrpahAtEnd(selBoundaryEntry)) {
        const paragraphWasCreated = AlineTransforms.ensureParagraphAtEnd(editor, {
          authorId,
          containerEntry: selBoundaryEntry,
          editingMode,
        });
        if (paragraphWasCreated) return;
      }

      return AlineTransforms.moveSelToParagraphEdge(editor, { direction, edge });
    }

    /**
     * Check if we should create an empty paragraph at the end of the selection boundary.
     */
    function shouldEnsureParagrpahAtEnd(selBoundaryEntry: NodeEntry<SelBoundaryNode>) {
      if (
        !editor.selection ||
        edge !== 'both' ||
        direction !== 'forward' ||
        eventSource !== SelEventSource.KEYBOARD ||
        (editingMode !== EditingMode.EDITING && editingMode !== EditingMode.SUGGESTING)
      )
        return false;

      const [{ children: selBoundaryChildren }, selBoundaryPath] = selBoundaryEntry;
      const lastBlockPath = selBoundaryPath.concat(selBoundaryChildren.length - 1);

      return Path.isDescendant(editor.selection.focus.path, lastBlockPath);
    }
  },

  /**
   * Utility for mouse down select to select the correct Range based on rapid-clicking action.
   */
  resolveSelectionFromMouseEventPoints(
    editor: Editor,
    options: {
      consecutiveClickCount: number;
      lastMouseDownPoint: Point | null;
      lastMouseDownTimestamp: number | null;
      mouseEventPoints: Range;
      lineEdge?: LinePointPosition;
    }
  ): { consecutiveClickCount: number; range: Range } {
    const {
      consecutiveClickCount,
      lastMouseDownPoint,
      lastMouseDownTimestamp,
      lineEdge,
      mouseEventPoints,
    } = options;

    const now = performance.now();
    const isRapidClick = lastMouseDownTimestamp
      ? now - lastMouseDownTimestamp < CLICK_THRESHOLD
      : false;
    const isSamePoint = lastMouseDownPoint
      ? Point.equals(lastMouseDownPoint, mouseEventPoints.focus)
      : false;

    const { anchor: clickPoint } = mouseEventPoints;
    const nextConsecutiveClickCount = isRapidClick && isSamePoint ? consecutiveClickCount + 1 : 1;

    const clickCount =
      nextConsecutiveClickCount > 3
        ? (nextConsecutiveClickCount % 3) + 1
        : nextConsecutiveClickCount;

    switch (clickCount) {
      case 3: {
        const nodeEntry = Editor.above(editor, {
          at: mouseEventPoints.focus.path,
          match: isParagraphElement,
        });
        if (!nodeEntry)
          return { consecutiveClickCount: nextConsecutiveClickCount, range: mouseEventPoints };

        const [, paragraphPath] = nodeEntry;
        return {
          consecutiveClickCount: nextConsecutiveClickCount,
          range: Editor.range(editor, paragraphPath),
        };
      }
      case 2: {
        const paragraphEntry = Editor.above(editor, { at: clickPoint, match: isParagraphElement });
        if (paragraphEntry && Queries.nodeIsEmpty(paragraphEntry[0]))
          return { consecutiveClickCount: nextConsecutiveClickCount, range: mouseEventPoints };

        const pointPosition = detectPointPositionRelativeToWord(editor, clickPoint);

        if (lineEdge === 'start' || pointPosition.isAtWordStart) {
          const newFocus = Editor.after(editor, clickPoint, { distance: 1, unit: 'word' });
          return {
            consecutiveClickCount: nextConsecutiveClickCount,
            range: { anchor: clickPoint, focus: newFocus ? newFocus : clickPoint },
          };
        } else if (lineEdge === 'end' || pointPosition.isAtWordEnd) {
          const newFocus = Editor.before(editor, clickPoint, { distance: 1, unit: 'word' });
          return {
            consecutiveClickCount: nextConsecutiveClickCount,
            range: { anchor: clickPoint, focus: newFocus ? newFocus : clickPoint },
          };
        }

        const newAnchor = Editor.before(editor, clickPoint, { distance: 1, unit: 'word' });
        const newFocus = Editor.after(editor, clickPoint, { distance: 1, unit: 'word' });

        return {
          consecutiveClickCount: nextConsecutiveClickCount,
          range: { anchor: newAnchor ?? clickPoint, focus: newFocus ?? clickPoint },
        };
      }
      case 1:
      case 0:
      default:
        return { consecutiveClickCount: nextConsecutiveClickCount, range: mouseEventPoints };
    }
  },
};

interface AdjacentLineWidthReturn {
  absWidthToFocus: number;
  relWidthToNewFocus: number;
}

/**
 * Utility function for determining the width to the new focus *relative to the adjacent line's left edge*.
 */
function getAdjacentLineWidth(
  editor: Editor,
  absWidthToFocus: ReturnType<typeof Queries.getWidthToFocus>,
  adjacentLineEntry: NodeEntry<RenderModelLine> | void
): AdjacentLineWidthReturn {
  if (!adjacentLineEntry) return { absWidthToFocus, relWidthToNewFocus: 0 };
  const lineLeftEdgePx = Queries.getLineLeftEdgePx(editor, adjacentLineEntry);

  return {
    absWidthToFocus,
    relWidthToNewFocus: absWidthToFocus - lineLeftEdgePx,
  };
}

interface AdjacentLineEntryReturn extends AdjacentLineWidthReturn {
  adjacentLineEntry: NodeEntry<RenderModelLine> | void;
}

function getAdjacentLineEntry(
  editor: AlineClientEditor,
  rMLineEntry: NodeEntry<RenderModelLine>,
  direction: 'forward' | 'backward'
): AdjacentLineEntryReturn {
  const [, rMFocusLinePath] = rMLineEntry;

  const [, to] =
    direction === 'backward' ? Queries.rMFirst(editor, []) : Queries.rMLast(editor, []);

  const adjacentRowEntries = [
    ...Queries.renderModelNodes<RenderModelTableRow>(editor, {
      at: [rMFocusLinePath, to],
      match: isTableRowElement,
      reverse: direction === 'backward',
    }),
  ];

  const adjacentRMRowEntry = adjacentRowEntries.find(([, path]) =>
    direction === 'backward'
      ? Path.isBefore(path, rMFocusLinePath)
      : Path.isAfter(path, rMFocusLinePath)
  );

  const rMFocusCellEntry = Queries.renderModelAbove<RenderModelTableCell>(editor, {
    at: rMFocusLinePath,
    match: isTableCellElement,
  });

  if (!rMFocusCellEntry) {
    if (!adjacentRMRowEntry) return getDefaultReturn();

    const [, adjacentRMRowPath] = adjacentRMRowEntry;
    const isFocusLineAbuttingAdjacentRow = Queries.rMPathIsAbutting(
      editor,
      rMFocusLinePath,
      adjacentRMRowPath
    );
    if (!isFocusLineAbuttingAdjacentRow) return getDefaultReturn();

    return getAbuttingTableRowLine(
      editor,
      rMLineEntry,
      [adjacentRMRowEntry],
      getDefaultReturn,
      direction
    );
  }

  const [, rMFocusCellPath] = rMFocusCellEntry;
  const rMFocusRowEntry = Queries.renderModelAbove<RenderModelTableRow>(editor, {
    at: rMFocusCellPath,
    match: isTableRowElement,
  });
  const firstOrLastCellParaEntry = Queries.getParagraphEntryInRoot<RenderModelParagraph>(
    rMFocusCellEntry,
    {
      item: direction === 'backward' ? 'first' : 'last',
    }
  );
  if (!rMFocusRowEntry || !firstOrLastCellParaEntry) return getDefaultReturn();

  const [firstOrLastPara, firstOrLastCellParaPath] = firstOrLastCellParaEntry;
  const firstOrLastLineIndex = direction === 'backward' ? 0 : firstOrLastPara.children.length - 1;
  const firstOrLastLinePath = [...firstOrLastCellParaPath, firstOrLastLineIndex];
  const isFocusLineFirstOrLast = Path.equals(rMFocusLinePath, firstOrLastLinePath);
  if (!isFocusLineFirstOrLast) {
    if (!adjacentRMRowEntry) return getDefaultReturn();

    const [, adjacentRMRowPath] = adjacentRMRowEntry;
    const isFocusLineAbuttingAdjacentRow = Queries.rMPathIsAbutting(
      editor,
      rMFocusLinePath,
      adjacentRMRowPath
    );
    if (!isFocusLineAbuttingAdjacentRow) return getDefaultReturn();

    return getAbuttingTableRowLine(
      editor,
      rMLineEntry,
      [adjacentRMRowEntry],
      getDefaultReturn,
      direction
    );
  }

  // rMFocus line is the first or last in rMFocusCell.
  const [, rMFocusRowPath] = rMFocusRowEntry;
  const cMFocusRowPath = Queries.getCMPathFromRMPath(editor, rMFocusRowPath);
  const rMSiblingRowEntries = adjacentRowEntries.filter(([, rMPath]) => {
    const isRMPathAdjacentToLine =
      direction === 'backward'
        ? Path.isBefore(rMPath, rMFocusLinePath)
        : Path.isAfter(rMPath, rMFocusLinePath);
    const adjacentRowCMPath = Queries.getCMPathFromRMPath(editor, rMPath);
    // Path.equals b/c the rMRow can be split across pages and still have the same cMPath.
    const isSibling =
      Path.equals(cMFocusRowPath, adjacentRowCMPath) ||
      Path.isSibling(cMFocusRowPath, adjacentRowCMPath);

    return isRMPathAdjacentToLine && isSibling;
  });
  if (!rMSiblingRowEntries.length) return getDefaultReturn(rMFocusRowPath);

  return getAbuttingTableRowLine(
    editor,
    rMLineEntry,
    rMSiblingRowEntries,
    getDefaultReturn,
    direction
  );

  function getDefaultReturn(path = rMFocusLinePath): AdjacentLineEntryReturn {
    const adjacentLineEntries = [
      ...Queries.renderModelNodes<RenderModelLine>(editor, {
        at: [path, to],
        match: isRenderModelLine,
        reverse: direction === 'backward',
        voids: true,
      }),
    ];

    const adjacentLineEntry = adjacentLineEntries.find(([, rMLinePath]) =>
      direction === 'backward' ? Path.isBefore(rMLinePath, path) : Path.isAfter(rMLinePath, path)
    );

    const absWidthToFocus = Queries.getWidthToFocus(editor, rMLineEntry);

    return {
      adjacentLineEntry,
      ...getAdjacentLineWidth(editor, absWidthToFocus, adjacentLineEntry),
    };
  }
}

function getAbuttingTableRowLine(
  editor: AlineClientEditor,
  rMLineEntry: NodeEntry<RenderModelLine>,
  abuttingRMTableRowEntries: NodeEntry<RenderModelTableRow>[],
  getDefaultReturn: (path?: Path) => AdjacentLineEntryReturn,
  direction: 'forward' | 'backward'
): AdjacentLineEntryReturn {
  for (const abuttingRMTableRowEntry of abuttingRMTableRowEntries) {
    const [, abuttingRMTableRowPath] = abuttingRMTableRowEntry;
    const rMTableEntry = Queries.renderModelAbove<RenderModelTable>(editor, {
      at: abuttingRMTableRowPath,
      match: isTableElement,
    });
    if (!rMTableEntry) continue;

    const xPosition = Queries.getWidthToFocus(editor, rMLineEntry);
    const abuttingRMTableCellEntry = Queries.getRMTableCellAtAbsoluteXPosition(editor, {
      rMTableRowEntry: abuttingRMTableRowEntry,
      xPosition,
    });
    if (!abuttingRMTableCellEntry) continue;
    const [abuttingRMTableCell, abuttingRMTableCellPath] = abuttingRMTableCellEntry;

    // Do not select a non-first vertically merged cell
    if (!abuttingRMTableCell.verticalMergeIsStart) continue;

    const rMCellLineEntries = Queries.renderModelNodes(editor, {
      at: abuttingRMTableCellPath,
      match: isRenderModelLine,
      reverse: direction === 'backward',
      voids: true,
    });
    const rMCellLineEntry = rMCellLineEntries?.next().value;

    // We can't find a line in the cell (might be an empty split cell) so keep searching for a line in the next abutting row.
    if (rMCellLineEntry)
      return {
        adjacentLineEntry: rMCellLineEntry,
        ...getAdjacentLineWidth(editor, xPosition, rMCellLineEntry),
      };
  }

  // Didn't find a line in any abutting row.  Select the line after the last abutting row.
  const [, lastRMTableRowPath] = abuttingRMTableRowEntries[abuttingRMTableRowEntries.length - 1];

  return getDefaultReturn(lastRMTableRowPath);
}

function detectPointPositionRelativeToWord(
  editor: Editor,
  clickPoint: Point
): {
  isAtWordEnd: boolean;
  isAtWordStart: boolean;
} {
  const textNode = Editor.node(editor, clickPoint.path);
  if (!textNode || !Text.isText(textNode[0]))
    return {
      isAtWordEnd: false,
      isAtWordStart: false,
    };

  const [{ text }] = textNode;
  const { offset } = clickPoint;

  return {
    isAtWordEnd: offset === text.length || text[offset] === ' ',
    isAtWordStart: offset === 0 || text[offset - 1] === ' ',
  };
}
